import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import { notification } from '@spark/ui-components/utils/notification';
import { syncDepotsAnexo } from '@spark/services';
import { getAnexosUploadColumns } from './tanStackColumns';

const AnexosUploadCard = () => {
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [addingFiles, setAddingFiles] = useState(false);
  const minLoadingMsRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [statusById, setStatusById] = useState({});
  const [errorById, setErrorById] = useState({});
  const fileInputRef = useRef(null);

  const getRowError = useCallback((row) => {
    const tipo = String(row?.tipo || '').toLowerCase();

    if (!['coleta', 'devolucao', 'cte', 'ric'].includes(tipo)) {
      return 'Tipo invalido (use Coleta, Devolucao, CTE ou RIC)';
    }

    if (tipo === 'cte') {
      if (!row?.numeroContainer) {
        return 'Numero do container ausente';
      }
      return '';
    }

    if (!row?.nomeMotorista) {
      return 'Nome do motorista ausente';
    }

    if (!row?.cpfMotorista) {
      return 'CPF do motorista ausente';
    }

    if (String(row.cpfMotorista).replace(/\D/g, '').length !== 11) {
      return 'CPF do motorista invalido';
    }

    if (!row?.numeroContainer) {
      return 'Numero do container ausente';
    }

    return '';
  }, []);

  const rows = useMemo(() => {
    return files.map((file) => {
      const nomeOriginal = file.name || '';
      const id = `${file.name}-${file.size}-${file.lastModified}`;
      const baseName = nomeOriginal.replace(/\.[^/.]+$/, '');
      const parts = baseName.split('_');

      const tipo = parts[0] || '';

      let nomeMotorista = '';
      let cpfMotorista = '';
      let numeroContainer = '';

      if (String(tipo).toLowerCase() === 'cte') {
        numeroContainer = parts[1] || '';
      } else {
        nomeMotorista = parts[1] || '';
        const cpfMotoristaRaw = parts[2] || '';
        cpfMotorista = cpfMotoristaRaw.replace(/\D/g, '');
        numeroContainer = parts.length > 3 ? parts.slice(3).join('_') : '';
      }

      const row = {
        id,
        file,
        nomeOriginal,
        tipo,
        nomeMotorista,
        cpfMotorista,
        numeroContainer
      };

      const erro = getRowError(row);
      const serverError = errorById[id];
      const displayError = serverError || erro;

      return {
        ...row,
        validationError: erro,
        erro: displayError,
        vazio: displayError ? 'Sim' : undefined,
        status: statusById[id] || (displayError ? 'Erro' : 'Pendente'),
      };
    });
  }, [files, getRowError, statusById, errorById]);

  const allPageSelected = useMemo(
    () => rows.length > 0 && rows.every((item) => selectedIds.includes(item.id)),
    [rows, selectedIds]
  );

  const selectAllDisabled = useMemo(() => rows.length === 0, [rows]);

  const handleToggleSelect = useCallback((row) => {
    if (!row) return;
    if (statusById[row.id] === 'Enviado') return;

    setSelectedIds((prev) => {
      const exists = prev.includes(row.id);
      if (exists) return prev.filter((id) => id !== row.id);
      return [...prev, row.id];
    });
  }, [statusById]);

  const handleToggleSelectAll = useCallback(() => {
    if (!rows.length) return;
    const selectableRows = rows.filter((row) => row.status !== 'Enviado');
    if (!selectableRows.length) return;

    setSelectedIds((prev) => {
      const allSelected = selectableRows.every((item) => prev.includes(item.id));
      if (allSelected) return prev.filter((id) => !selectableRows.some((row) => row.id === id));
      const newOnes = selectableRows.filter((item) => !prev.includes(item.id)).map((item) => item.id);
      return [...prev, ...newOnes];
    });
  }, [rows]);

  const handleVisualizar = useCallback((row) => {
    if (!row?.file) return;
    const url = URL.createObjectURL(row.file);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }, []);

  const handleExcluir = useCallback((row) => {
    if (!row?.id) return;
    setFiles((prev) =>
      prev.filter((file) => `${file.name}-${file.size}-${file.lastModified}` !== row.id)
    );
    setSelectedIds((prev) => prev.filter((id) => id !== row.id));
    setStatusById((prev) => {
      if (!prev[row.id]) return prev;
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    setErrorById((prev) => {
      if (!prev[row.id]) return prev;
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
  }, []);

  const columns = useMemo(
    () =>
      getAnexosUploadColumns(
        selectedIds,
        handleToggleSelect,
        handleToggleSelectAll,
        allPageSelected,
        selectAllDisabled,
        handleVisualizar,
        handleExcluir,
        new Set(rows.filter((row) => row.status === 'Enviado').map((row) => row.id))
      ),
    [
      selectedIds,
      handleToggleSelect,
      handleToggleSelectAll,
      allPageSelected,
      selectAllDisabled,
      handleVisualizar,
      handleExcluir,
      rows
    ]
  );

  const handleEnviar = async () => {
    if (!rows.length) {
      notification.warning('Selecione ao menos um arquivo para enviar.');
      return;
    }

    if (!selectedIds.length) {
      notification.warning('Selecione ao menos um arquivo na tabela.');
      return;
    }

    const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
    const invalidRows = selectedRows.filter((row) => row.validationError);
    if (invalidRows.length) {
      notification.error('Existem arquivos com nome fora do padrao esperado.');
      return;
    }

    setStatusById((prev) => {
      const next = { ...prev };
      selectedRows.forEach((row) => {
        next[row.id] = 'Enviando';
      });
      return next;
    });
    setErrorById((prev) => {
      const next = { ...prev };
      selectedRows.forEach((row) => {
        delete next[row.id];
      });
      return next;
    });

    const groups = new Map();
    selectedRows.forEach((row) => {
      const tag = String(row.tipo || '').toUpperCase();
      const key = `${row.numeroContainer}||${row.cpfMotorista}||${tag}`;
      if (!groups.has(key)) {
        groups.set(key, {
          container: row.numeroContainer,
          cpfMotorista: row.cpfMotorista,
          arquivos: [],
          tag,
          rowIds: [],
        });
      }
      const group = groups.get(key);
      group.arquivos.push(row.file);
      group.rowIds.push(row.id);
    });

    setSending(true);
    let successCount = 0;
    let errorCount = 0;
    let lastErrorMessage = '';
    const sentIds = new Set();

    for (const group of groups.values()) {
      try {
        await syncDepotsAnexo({
          container: group.container,
          cpfMotorista: group.cpfMotorista,
          arquivos: group.arquivos,
          tag: group.tag,
        });
        successCount += group.rowIds.length;
        group.rowIds.forEach((id) => sentIds.add(id));
        setStatusById((prev) => {
          const next = { ...prev };
          group.rowIds.forEach((id) => {
            next[id] = 'Enviado';
          });
          return next;
        });
        setErrorById((prev) => {
          const next = { ...prev };
          group.rowIds.forEach((id) => {
            delete next[id];
          });
          return next;
        });
      } catch (error) {
        console.error('Erro ao enviar anexos:', error);
        errorCount += group.rowIds.length;
        lastErrorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.mensagem ||
          error?.message ||
          'Erro ao enviar anexos.';
        const errorText =
          error?.response?.data?.message ||
          error?.response?.data?.mensagem ||
          error?.message ||
          error?.response?.data?.error ||
          'Erro ao enviar anexos.';

        setStatusById((prev) => {
          const next = { ...prev };
          group.rowIds.forEach((id) => {
            next[id] = 'Erro';
          });
          return next;
        });
        setErrorById((prev) => {
          const next = { ...prev };
          group.rowIds.forEach((id) => {
            next[id] = errorText;
          });
          return next;
        });
      }
    }

    if (successCount && !errorCount) {
      notification.success('Anexos enviados com sucesso.');
    } else if (successCount && errorCount) {
      notification.warning('Alguns anexos foram enviados e outros falharam.');
    } else if (errorCount) {
      notification.error(lastErrorMessage || 'Erro ao enviar anexos.');
    }

    if (sentIds.size) {
      setFiles((prev) =>
        prev.filter((file) => {
          const id = `${file.name}-${file.size}-${file.lastModified}`;
          return !sentIds.has(id);
        })
      );
      setSelectedIds((prev) => prev.filter((id) => !sentIds.has(id)));
      setStatusById((prev) => {
        const next = { ...prev };
        sentIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
      setErrorById((prev) => {
        const next = { ...prev };
        sentIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    }

    setSending(false);
  };

  return (
    <Card>
      <CardBody>
        <Row className="mb-3">
          <Col md={12} className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Enviar anexos</h5>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(event) => {
                  const newFiles = Array.from(event.target.files || []);
                  if (!newFiles.length) return;
                  setAddingFiles(true);
                  const minDelay = 300;
                  const start = Date.now();
                  if (minLoadingMsRef.current) {
                    clearTimeout(minLoadingMsRef.current);
                    minLoadingMsRef.current = null;
                  }

                  setTimeout(() => {
                    setFiles((prev) => {
                      const existingIds = new Set(
                        prev.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
                      );
                      const merged = [...prev];
                      const nextStatus = {};
                      const nextErrors = {};
                      newFiles.forEach((file) => {
                        const id = `${file.name}-${file.size}-${file.lastModified}`;
                        if (!existingIds.has(id)) {
                          existingIds.add(id);
                          merged.push(file);
                          nextStatus[id] = 'Pendente';
                          nextErrors[id] = '';
                        }
                      });
                      if (Object.keys(nextStatus).length) {
                        setStatusById((current) => ({ ...current, ...nextStatus }));
                      }
                      if (Object.keys(nextErrors).length) {
                        setErrorById((current) => ({ ...current, ...nextErrors }));
                      }
                      return merged;
                    });
                    event.target.value = '';
                    const elapsed = Date.now() - start;
                    const remaining = Math.max(minDelay - elapsed, 0);
                    minLoadingMsRef.current = setTimeout(() => {
                      setAddingFiles(false);
                      minLoadingMsRef.current = null;
                    }, remaining);
                  }, 0);
                }}
              />
              <ButtonWithLoading
                variant="outline-primary"
                loading={addingFiles}
                onClick={() => fileInputRef.current?.click()}
              >
                Adicionar anexos
              </ButtonWithLoading>
              <ButtonWithLoading
                variant="primary"
                loading={sending}
                onClick={handleEnviar}
              >
                Enviar anexos
              </ButtonWithLoading>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={3}>
            <div className="text-muted">
              <strong>Padrão esperado - Coleta / Devolucao / RIC:</strong>
              <br />
              Tipo_NomeMotorista_CpfMotorista_Container
              <br />
              Ex: Coleta_Pedro_12345678901_BRHU5637277.png
            </div>
          </Col>

          <Col md={6}>
            <div className="text-muted">
              <strong>Padrão esperado - CTE:</strong>
              <br />
              CTE_Container
              <br />
              Ex: CTE_BRHU5637277.pdf
            </div>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <TanStackTable
              columns={columns}
              data={rows}
              isSortable={true}
              withPagination={true}
              manualPagination={false}
              totalItems={rows.length}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              initialState={{
                pageIndex: 0,
                pageSize: 10
              }}
              enableColumnConfig={true}
              columnConfigKey="anexos-upload-columns"
              enableExportExcel={false}
              enableRowColors={true}
              className="mt-3"
            />
          </Col>
        </Row>

      </CardBody>
    </Card>
  );
};

export default AnexosUploadCard;
